import { Document, View } from "@react-pdf/renderer";
import type { ReactElement } from "react";

import { DiligencesSection } from "src/core/components/pdf/sections/Diligences";
import { GraphSection } from "src/core/components/pdf/sections/Graph";
import { LabSection } from "src/core/components/pdf/sections/Lab";
import { MissionsSection } from "src/core/components/pdf/sections/Missions";
import { SommaireSection } from "src/core/components/pdf/sections/Sommaire";
import { StakeholdersSection } from "src/core/components/pdf/sections/Stakeholders";
import { SynthesisSection } from "src/core/components/pdf/sections/Synthesis";
import { CoverPage } from "src/core/components/pdf/shared/CoverPage";
import { PdfFooter } from "src/core/components/pdf/shared/PdfFooter";
import { PdfHeader } from "src/core/components/pdf/shared/PdfHeader";
import { PdfPage } from "src/core/components/pdf/shared/PdfPage";
import { FolderFileType } from "src/core/components/pdf/shared/types";
import { IRiskOption, ISelectOption } from "src/core/constants/options";
import { CartographyRisks } from "src/core/contexts/FolderContext";
import { User } from "src/core/gql/graphql";

type FolderPdfDocumentProps = {
  currentUser: User;
  folderFile: FolderFileType;
  folderRisks: CartographyRisks;
  /** Image PNG du graphe Cytoscape (data URL) si exportée depuis la vue dossier, sinon fallback SVG. */
  graphImageDataUrl?: string | null;
  missionTypeWithRiskOptions: IRiskOption[];
  activityWithRiskOptions?: IRiskOption[];
  countryWithRiskOptions?: IRiskOption[];
  legalTypesWithRiskOptions?: IRiskOption[];
  legalStatusOptions?: ISelectOption[];
  legalStatusPPHOptions?: ISelectOption[];
};

export const FolderPdfDocument = ({
  currentUser,
  folderFile,
  folderRisks,
  graphImageDataUrl = null,
  missionTypeWithRiskOptions,
  activityWithRiskOptions = [],
  countryWithRiskOptions = [],
  legalTypesWithRiskOptions = [],
  legalStatusOptions = [],
  legalStatusPPHOptions = [],
}: FolderPdfDocumentProps): ReactElement => {
  const isEI = folderFile.fileCustomerFolder?.customer?.isPerson;

  const stakeholders = folderFile.fileCustomerFolder?.stakeholders ?? [];
  const sortedStakeholders = [...stakeholders].sort((a, b) => {
    if (a.isFolderCustomer && !b.isFolderCustomer) return -1;
    if (!a.isFolderCustomer && b.isFolderCustomer) return 1;
    return 0;
  });

  const shouldRenderLabSection = isEI || (!isEI && sortedStakeholders.length % 2 === 0);

  return (
    <Document>
      {/* Page de garde */}
      <CoverPage titleTranslationKey={"pdf.sections.coverPage.title"} />

      {/* Sommaire */}
      <PdfPage>
        <PdfHeader />
        <SommaireSection folderFile={folderFile} />
        <PdfFooter folderFile={folderFile} currentUser={currentUser} />
      </PdfPage>

      {/* Première page / récapitulatif */}
      <PdfPage>
        <PdfHeader />
        <View style={{ paddingTop: 20 }}>
          <SynthesisSection
            folderFile={folderFile}
            folderRisks={folderRisks}
            graphImageDataUrl={graphImageDataUrl}
          />

          {/* Page pour le graphe (pas visible si EI) */}
          {!isEI && graphImageDataUrl && <GraphSection graphImageDataUrl={graphImageDataUrl} />}
        </View>
        <PdfFooter folderFile={folderFile} currentUser={currentUser} />
      </PdfPage>

      {/* Page pour les missions */}
      <PdfPage>
        <PdfHeader />

        <MissionsSection
          folderFile={folderFile}
          missionTypeWithRiskOptions={missionTypeWithRiskOptions}
        />

        <PdfFooter folderFile={folderFile} currentUser={currentUser} />
      </PdfPage>

      {/* Pages pour la section Personnes (2 personnes par page) */}
      {/* Si le nombre de stakeholder est impair, alors on affiche le questionnaire LAB sur la dernière page */}
      <StakeholdersSection
        currentUser={currentUser}
        folderFile={folderFile}
        sortedStakeholders={sortedStakeholders}
        activityWithRiskOptions={activityWithRiskOptions}
        countryWithRiskOptions={countryWithRiskOptions}
        legalTypesWithRiskOptions={legalTypesWithRiskOptions}
        legalStatusOptions={legalStatusOptions}
        legalStatusPPHOptions={legalStatusPPHOptions}
      />

      {/* Page pour la section LAB */}
      {/* Si c'est une EI ou que le nombre de stakeholders est pair, alors elle est affichée sur une page distincte */}
      {shouldRenderLabSection && (
        <PdfPage>
          <PdfHeader />
          <LabSection folderFile={folderFile} />
          <PdfFooter folderFile={folderFile} currentUser={currentUser} />
        </PdfPage>
      )}

      {/* Pages pour la section Diligences */}
      {folderFile?.diligences.length > 0 && (
        <PdfPage>
          <PdfHeader />
          <DiligencesSection folderFile={folderFile} />
          <PdfFooter folderFile={folderFile} currentUser={currentUser} />
        </PdfPage>
      )}
    </Document>
  );
};
